import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { Modal, Field, Th, Td } from "./Customers";
import { Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function TenantsAdmin() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/tenants").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]" data-testid="tenants-page">
      <PageHeader
        title="Workspaces"
        subtitle={user?.role === "super_admin"
          ? "All workspaces on this Gr8Boost ERP platform. White-label each one independently."
          : "Your workspace settings."}
        action={
          (user?.role === "super_admin" || user?.role === "tenant_admin") && (
            <button onClick={() => setOpen(true)} data-testid="new-tenant-btn"
              className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm font-medium rounded-sm">
              <Plus size={14} /> New workspace
            </button>
          )
        }
      />
      <div className="mt-8 border border-[#0A0A0A] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
            <tr><Th>Slug</Th><Th>Name</Th><Th>Country</Th><Th>Currency</Th><Th>Brand</Th><Th>Modules</Th></tr>
          </thead>
          <tbody>
            {rows.map((t, i) => (
              <tr key={t.id} data-testid={`tenant-row-${i}`} className="border-b border-[#DEE2E6] hover:bg-[#F8F9FA]">
                <Td className="font-mono text-xs">{t.slug}</Td>
                <Td><div className="font-medium">{t.name}</div><div className="text-[#495057] text-xs">{t.legal_name}</div></Td>
                <Td>{t.country}</Td>
                <Td>{t.currency}</Td>
                <Td>
                  <div className="flex gap-1">
                    <span className="inline-block w-5 h-5 border border-[#DEE2E6]" style={{ background: t.brand_color }} title={t.brand_color} />
                    <span className="inline-block w-5 h-5 border border-[#DEE2E6]" style={{ background: t.accent_color }} title={t.accent_color} />
                  </div>
                </Td>
                <Td className="text-xs text-[#495057]">{(t.modules_enabled || []).join(", ")}</Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#495057]">No workspaces.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <TenantForm onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function TenantForm({ onClose, onSaved }) {
  const [f, setF] = useState({ slug: "", name: "", legal_name: "", country: "", currency: "USD", brand_color: "#0033A0", accent_color: "#FF4F00" });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setErr(""); setSaving(true);
    try { await api.post("/tenants", f); onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="New tenant" onClose={onClose} testid="tenant-modal" wide>
      <form onSubmit={save} className="grid grid-cols-2 gap-4" data-testid="tenant-form">
        <Field label="Slug (a-z, 0-9, -)" required value={f.slug} onChange={(v) => setF({ ...f, slug: v.toLowerCase() })} testid="tenant-slug" />
        <Field label="Name" required value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="tenant-name" />
        <div className="col-span-2"><Field label="Legal name" value={f.legal_name} onChange={(v) => setF({ ...f, legal_name: v })} testid="tenant-legal" /></div>
        <Field label="Country" value={f.country} onChange={(v) => setF({ ...f, country: v })} testid="tenant-country" />
        <Field label="Currency" value={f.currency} onChange={(v) => setF({ ...f, currency: v })} testid="tenant-currency" />
        <Field label="Brand color" value={f.brand_color} onChange={(v) => setF({ ...f, brand_color: v })} testid="tenant-brand-color" />
        <Field label="Accent color" value={f.accent_color} onChange={(v) => setF({ ...f, accent_color: v })} testid="tenant-accent-color" />
        {err && <div className="col-span-2 text-sm text-[#D90429]">{err}</div>}
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" disabled={saving} data-testid="tenant-save"
            className="px-4 py-2 text-sm bg-[#0A0A0A] hover:bg-[#FF4F00] text-white">Save</button>
        </div>
      </form>
    </Modal>
  );
}
